import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Alert, Button, Link, TextField } from '@mui/material';
import { Card, CardContent, CardHeader, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { GuestGuard } from 'src/guards/guest-guard';
import { IssuerGuard } from 'src/guards/issuer-guard';
import { useAuth } from 'src/hooks/use-auth';
import { useMounted } from 'src/hooks/use-mounted';
import { usePageView } from 'src/hooks/use-page-view';
import { useRouter } from 'src/hooks/use-router';
import { useSearchParams } from 'src/hooks/use-search-params';
import { Layout as AuthLayout } from 'src/layouts/auth/classic-layout';
import { paths } from 'src/paths';
import { Issuer } from 'src/utils/auth';
import { startLoading, stopLoading } from 'src/utils/loading';
import { useTranslation } from 'react-i18next';
import { appStr } from 'src/locales/app_strings';
import { tokens } from 'src/locales/tokens';
import debounce from 'lodash/debounce'; // Import debounce function

const initialValues = {
  username: localStorage.getItem('cache_username') || '',
  password: '',
  submit: null,
};

const getValidationSchema = (t) => {
  return Yup.object({
    username: Yup.string().required(t(appStr.txt_052)),
    password: Yup.string().required(t(appStr.txt_053)),
  });
};

const Page = () => {
  const { t } = useTranslation();
  const validationSchema = getValidationSchema(t);

  const isMounted = useMounted();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { signIn } = useAuth();
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false, // Disable automatic change validation globally
    validateOnBlur: false, // Disable automatic blur validation globally
    onSubmit: async (values, helpers) => {
      startLoading();
      try {
        await signIn(values.username, values.password);
        // Save username into local storage
        localStorage.setItem('cache_username', values.username);
        if (isMounted()) {
          router.push(returnTo || paths.dashboard.index);
        }
      } catch (err) {
        if (isMounted()) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      } finally {
        stopLoading();
      }
    },
  });

  // Debounce function for validating fields
  const debouncedValidateField = useCallback(
    debounce((name) => {
      formik.validateField(name);
    }, 300), // 300ms delay
    [],
  );

  // Manually trigger validation on change and blur for individual fields
  const handleFieldChange = (e) => {
    formik.handleChange(e);
    //formik.validateField(e.target.name); // Validate only the current field
    debouncedValidateField(e.target.name);
  };

  const handleFieldBlur = (e) => {
    formik.handleBlur(e);
    //formik.validateField(e.target.name); // Validate only the current field on blur
  };

  usePageView();

  return (
    <>
      <Seo title="Login" />
      <Card elevation={16}>
        <CardHeader
          subheader={
            <Typography color="text.secondary" variant="body2">
              <Link
                component={RouterLink}
                to={paths.myauth.resetPassword}
                underline="hover"
                variant="subtitle2"
              >
                Quên mật khẩu?
              </Link>
              {/* <br />
              Don&apos;t have an account? &nbsp;
              <Link
                component={RouterLink}
                to={paths.auth.jwt.register}
                underline="hover"
                variant="subtitle2"
              >
                Register
              </Link> */}
            </Typography>
          }
          sx={{ pb: 0 }}
          title={t(tokens.nav.login)}
        />
        <CardContent>
          <form noValidate onSubmit={formik.handleSubmit}>
            <Stack spacing={3}>
              <TextField
                autoFocus
                error={!!(formik.touched.username && formik.errors.username)}
                fullWidth
                helperText={formik.touched.username && formik.errors.username}
                label="Mã đăng nhập"
                name="username"
                onBlur={handleFieldBlur}
                onChange={handleFieldChange}
                type="text"
                value={formik.values.username}
                variant='outlined'
                autoComplete="current-username"
              />
              <TextField
                error={!!(formik.touched.password && formik.errors.password)}
                fullWidth
                helperText={formik.touched.password && formik.errors.password}
                label="Mật khẩu"
                name="password"
                onBlur={handleFieldBlur}
                onChange={handleFieldChange}
                type="password"
                value={formik.values.password}
                variant='outlined'
                autoComplete="current-password"
              />
            </Stack>
            <Button
              disabled={formik.isSubmitting}
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              type="submit"
              variant="contained"
            >
              {t(tokens.nav.login)}
            </Button>
          </form>
        </CardContent>
      </Card>
      {formik.errors.submit && (
        <Stack spacing={3} sx={{ mt: 3 }}>
          <Alert severity="error">
            <div>{formik.errors.submit}</div>
          </Alert>
          {/* <AuthIssuer issuer={issuer} /> */}
        </Stack>
      )}
    </>
  );
};

Page.getLayout = (page) => (
  <IssuerGuard issuer={Issuer.JWT}>
    <GuestGuard>
      <AuthLayout>{page}</AuthLayout>
    </GuestGuard>
  </IssuerGuard>
);

export default Page;
